import React, { useState } from "react";
import { CheckCircleIcon, ExclamationTriangleIcon, TruckIcon, ArchiveBoxIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleSolidIcon } from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";
import { handleItem as handleItemApi } from "../../../api/inventory/itemLists";
import ItemHandlingDialog from "./item-handling-dialog";

/**
 * Generic ItemHandlingList component that can be used for both reports and work orders
 * @param {Object} props
 * @param {Object} props.itemList - The item list object with item_list_items
 * @param {boolean} props.isLoading - Loading state
 * @param {string} props.moduleType - Type of module ('report' or 'work_order')
 * @param {Function} props.onItemHandled - Callback when an item is handled
 * @param {boolean} props.showBanner - Whether to show the banner message (default: true)
 */
const ItemHandlingList = ({ itemList, isLoading = false, moduleType = "report", onItemHandled, showBanner = true }) => {
  const [handlingItem, setHandlingItem] = useState(null);
  const [availableStorages, setAvailableStorages] = useState([]);
  const [handlingDialog, setHandlingDialog] = useState({
    open: false,
    itemListItem: null,
    action: null
  });

  const handleItemAction = (itemListItem, action) => {
    setHandlingDialog({
      open: true,
      itemListItem,
      action
    });
  };

  const handleDialogConfirm = async (handlingData) => {
    setHandlingItem(handlingDialog.itemListItem.id);

    try {
      // Handle the specified quantity
      const result = await handleItemApi(
        itemList.id,
        handlingDialog.itemListItem.id,
        handlingData.action,
        handlingData.storageId,
        handlingData.quantity
      );

      if (onItemHandled) {
        onItemHandled(handlingDialog.itemListItem.id, result.data);
      }

      // Notify other pages (like report page) that data has been updated
      if (itemList?.module_param) {
        localStorage.setItem(`item_handling_update_${itemList.module_param}`, Date.now().toString());
      }

      // If there's remaining quantity, the API will have created a new item list item
      // We'll need to refetch or update the parent component
      if (result.data.remaining_quantity > 0) {
        // Notify parent to refetch data
        if (onItemHandled) {
          // This will trigger a refetch in the parent component
          onItemHandled('REFETCH_NEEDED', result.data);
        }

        // Also notify other pages of the update
        if (itemList?.module_param) {
          localStorage.setItem(`item_handling_update_${itemList.module_param}`, Date.now().toString());
        }
      }
    } catch (error) {
      console.error('Error handling item:', error);
      const errorMessage = error.response?.data?.errors
        ? error.response.data.errors.join(', ')
        : error.message || 'Ukjent feil';
      alert(`Det oppstod en feil ved håndtering av varen: ${errorMessage}`);
    } finally {
      setHandlingItem(null);
      setHandlingDialog({ open: false, itemListItem: null, action: null });
    }
  };

  const handleDialogCancel = () => {
    setHandlingDialog({ open: false, itemListItem: null, action: null });
  };

  const getItemStatus = (orderItem) => {
    if (orderItem.returned_to_storage) {
      const storageName = orderItem.storage?.title || 'lager';
      return {
        text: `Returnert til ${storageName}`,
        color: 'bg-green-100 text-green-800',
        icon: ArchiveBoxIcon
      };
    } else if (orderItem.left_in_vehicle) {
      const vehicleName = orderItem.storage?.title || 'kjøretøy';
      return {
        text: `Flyttet til ${vehicleName}`,
        color: 'bg-blue-100 text-blue-800',
        icon: TruckIcon
      };
    } else {
      return {
        text: 'Må håndteres',
        color: 'bg-yellow-100 text-yellow-800',
        icon: ExclamationTriangleIcon
      };
    }
  };
  if (isLoading) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
        <p className="text-gray-500">Laster varehåndteringsliste...</p>
      </div>
    );
  }

  // Don't show anything if no list exists
  if (!itemList) {
    return null;
  }

  // Get all items with their handling status
  const allItems = itemList.item_list_items || [];

  // Filter out already handled items for the action buttons
  const unhandledItems = allItems.filter((itemListItem) => {
    const orderItem = itemListItem.order_item || itemListItem;
    return !orderItem.returned_to_storage && !orderItem.left_in_vehicle;
  });

  // Show message if list exists but has no items at all
  if (allItems.length === 0) {
    return null; // Don't show empty list
  }

  return (
    <div className="space-y-4 mt-6">
      {showBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start flex-1">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mt-0.5" />
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-blue-800 mb-2">
                  Varehåndteringsliste opprettet
                </h3>
              <p className="text-sm text-blue-700">
                {unhandledItems.length > 0
                  ? `${unhandledItems.length} av ${allItems.length} produkter må håndteres. Listen oppdateres automatisk når vareforbruket endres.`
                  : `${allItems.length} produkter er håndtert og klare for neste steg.`
                }
              </p>
              </div>
            </div>
            <div className="ml-4 flex-shrink-0">
              <Link
                to={`/dashboard/inventory/item-handling/${itemList.id}`}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-md"
              >
                Håndter varer
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Produkt
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sm:hidden">
                Produkt/Info
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Antall
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Enhet
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Handling
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {allItems.map((itemListItem) => {
              const orderItem = itemListItem.order_item || itemListItem;
              const product = orderItem?.product || {};

              return (
                <tr key={itemListItem.id || orderItem?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium text-gray-900">
                        {product?.displayTitle ||
                          product?.title ||
                          orderItem?.title ||
                          `Produkt ${orderItem?.product_id || "Ukjent"}`}
                      </div>
                      {product?.item_number && (
                        <div className="text-sm text-gray-500">
                          Art.nr: {product.item_number}
                        </div>
                      )}
                      {/* Mobile view: Show quantity, unit inline */}
                      <div className="sm:hidden mt-2 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-700 text-center">
                            Antall: {itemListItem.quantity || 0} {orderItem?.unit?.title || ""}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                    <span className="font-medium">{itemListItem.quantity || 0}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                    <span className="text-sm text-gray-600">
                      {orderItem?.unit?.title || "-"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center hidden md:table-cell">
                    {(() => {
                      const status = getItemStatus(orderItem);
                      const StatusIcon = status.icon;
                      return (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${status.color}`}>
                          <StatusIcon className="h-4 w-4 mr-1" />
                          {status.text}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {orderItem.returned_to_storage || orderItem.left_in_vehicle ? (
                      <CheckCircleSolidIcon className="h-5 w-5 text-green-600 mx-auto" />
                    ) : (
                      <div className="flex items-center justify-center space-x-1">
                        <button
                          onClick={() => handleItemAction(itemListItem, 'return_to_storage')}
                          disabled={handlingItem === itemListItem.id}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-green-700 bg-green-100 hover:bg-green-200 rounded disabled:opacity-50"
                          title="Returner til lager"
                        >
                          <ArchiveBoxIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleItemAction(itemListItem, 'move_to_vehicle')}
                          disabled={handlingItem === itemListItem.id}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded disabled:opacity-50"
                          title="Flytt til kjøretøy"
                        >
                          <TruckIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Item Handling Dialog */}
      <ItemHandlingDialog
        open={handlingDialog.open}
        itemListItem={handlingDialog.itemListItem}
        action={handlingDialog.action}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />

      {itemList.status && (
        <div className="mt-4 text-sm text-gray-600">
          <span className="font-medium">Status:</span>{" "}
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
              itemList.status === "draft"
                ? "bg-gray-100 text-gray-800"
                : itemList.status === "pending_approval"
                ? "bg-yellow-100 text-yellow-800"
                : itemList.status === "approved"
                ? "bg-green-100 text-green-800"
                : itemList.status === "rejected"
                ? "bg-red-100 text-red-800"
                : "bg-gray-100 text-gray-800"
            }`}
          >
            {itemList.status === "draft"
              ? "Utkast"
              : itemList.status === "pending_approval"
              ? "Venter på godkjenning"
              : itemList.status === "approved"
              ? "Godkjent"
              : itemList.status === "rejected"
              ? "Avvist"
              : itemList.status}
          </span>
        </div>
      )}
    </div>
  );
};

export default ItemHandlingList;



